import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import Nav from "@/components/Nav";
import UploadStep from "@/components/UploadStep";
import { api, errMsg, inr, SIZES, THEMES } from "@/lib/api";

const STEP_NAMES = ["Size & style", "Photos", "AI magic"];
const AI_LINES = ["Checking print quality", "Setting aside near-duplicates & blurry shots", "Finding your highlights", "Detecting the occasion", "Writing captions", "Arranging pages"];

function Stepper({ step }) {
  return (
    <ol className="flex items-center gap-3 text-sm" data-testid="wizard-stepper">
      {STEP_NAMES.map((n, i) => (
        <li key={n} className={`flex items-center gap-2 ${i <= step ? "text-[#26232E]" : "text-[#8C8498]"}`}>
          <span className={`grid h-6 w-6 place-items-center rounded-full text-xs ${i < step ? "bg-[#789A82] text-white" : i === step ? "bg-[#3B5BDB] text-white" : "border border-[#E8E2D9]"}`}>
            {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
          </span>
          <span className="hidden sm:inline">{n}</span>
          {i < 2 && <span className="h-px w-6 bg-[#E8E2D9] sm:w-10" />}
        </li>
      ))}
    </ol>
  );
}

function SizeStep({ onNext }) {
  const [size, setSize] = useState("8x8");
  const [theme, setTheme] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/books", { size, theme, title: title.trim() || "Untitled Memories" });
      onNext(data);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="rise">
      <h1 className="font-serif text-4xl tracking-tight sm:text-5xl">Let's shape your book</h1>
      <div className="mt-8 max-w-md space-y-2">
        <label className="overline" htmlFor="book-title">Book title (optional — AI can suggest one)</label>
        <Input id="book-title" data-testid="wizard-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Ananya & Rohan" className="h-11 bg-white" />
      </div>
      <p className="overline mt-10">Size</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {SIZES.map((s) => (
          <button key={s.key} data-testid={`book-size-option-${s.key}`} onClick={() => setSize(s.key)}
            className={`paper flex items-center gap-5 rounded-lg p-5 text-left transition-colors ${size === s.key ? "!border-[#3B5BDB] ring-1 ring-[#3B5BDB]" : "hover:border-[#d9cfb6]"}`}>
            <div className="grid h-16 w-16 shrink-0 place-items-center">
              <div className="page-shadow bg-[#FAF5EF]" style={{ height: s.ratio >= 1 ? 56 / s.ratio : 56, width: s.ratio >= 1 ? 56 : 56 * s.ratio }} />
            </div>
            <div>
              <p className="font-semibold">{s.label}</p>
              <p className="text-sm text-[#8C8498]">{s.dims} · from {inr(s.from)}</p>
            </div>
          </button>
        ))}
      </div>
      <p className="overline mt-10">Theme</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <button data-testid="theme-option-auto" onClick={() => setTheme(null)}
          className={`paper rounded-lg p-4 text-left ${theme === null ? "!border-[#3B5BDB] ring-1 ring-[#3B5BDB]" : ""}`}>
          <Wand2 className="h-5 w-5 text-[#3B5BDB]" />
          <p className="mt-3 font-semibold">Let AI choose</p>
          <p className="text-xs text-[#8C8498]">Matched to your photos</p>
        </button>
        {THEMES.map((t) => (
          <button key={t.key} data-testid={`theme-option-${t.key}`} onClick={() => setTheme(t.key)}
            className={`paper rounded-lg p-4 text-left ${theme === t.key ? "!border-[#3B5BDB] ring-1 ring-[#3B5BDB]" : ""}`}>
            <span className="block h-5 w-5 rounded-full border border-[#E8E2D9]" style={{ background: t.swatch }} />
            <p className="mt-3 font-semibold">{t.label}</p>
            <p className="text-xs text-[#8C8498]">{t.desc}</p>
          </button>
        ))}
      </div>
      <button data-testid="wizard-next-btn" disabled={busy} onClick={go} className="btn-primary mt-12">Continue to photos</button>
    </div>
  );
}

function Processing({ bookId }) {
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const timer = useRef();
  useEffect(() => {
    const t = setInterval(() => setI((x) => Math.min(x + 1, AI_LINES.length - 1)), 2200);
    timer.current = setInterval(async () => {
      const { data } = await api.get(`/books/${bookId}`);
      if (data.status === "ready") { toast.success(data.ai_used ? "Your book is ready" : "Book arranged (AI unavailable — used smart defaults)"); nav(`/books/${bookId}/edit`); }
      if (data.status === "error") { toast.error("Something went wrong arranging your book. Please try again."); nav(`/new/${bookId}`); }
    }, 2500);
    return () => { clearInterval(t); clearInterval(timer.current); };
  }, [bookId, nav]);
  return (
    <div className="mx-auto max-w-lg py-16 text-center rise" data-testid="ai-processing-screen">
      <Sparkles className="mx-auto h-10 w-10 animate-pulse text-[#3B5BDB]" strokeWidth={1.3} />
      <h1 className="mt-6 font-serif text-4xl tracking-tight">Arranging your story…</h1>
      <ul className="mt-10 space-y-3 text-left">
        {AI_LINES.map((l, k) => (
          <li key={l} className={`flex items-center gap-3 text-sm transition-opacity duration-500 ${k <= i ? "opacity-100" : "opacity-30"}`}>
            <span className={`h-2 w-2 rounded-full ${k < i ? "bg-[#789A82]" : k === i ? "bg-[#3B5BDB] animate-pulse" : "bg-[#E8E2D9]"}`} />
            {l}
          </li>
        ))}
      </ul>
      <p className="mt-10 text-xs text-[#8C8498]">Usually under a minute. You'll be able to change everything afterwards.</p>
    </div>
  );
}

export default function NewBook() {
  const { bookId } = useParams();
  const nav = useNavigate();
  const [step, setStep] = useState(bookId ? 1 : 0);
  const [book, setBook] = useState(null);
  useEffect(() => {
    if (bookId) api.get(`/books/${bookId}`).then((r) => { setBook(r.data); if (r.data.status === "processing") setStep(2); });
  }, [bookId]);
  const curate = async () => {
    try { await api.post(`/books/${book.id}/curate`); setStep(2); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <Stepper step={step} />
        <div className="mt-10">
          {step === 0 && <SizeStep onNext={(b) => { setBook(b); nav(`/new/${b.id}`, { replace: true }); setStep(1); }} />}
          {step === 1 && book && <UploadStep book={book} onDone={curate} />}
          {step === 2 && book && <Processing bookId={book.id} />}
        </div>
      </main>
    </div>
  );
}
